/**
 * White Labs — catalogued in full from whitelabs.com, both the year-round Core
 * strains and the seasonal Vault releases, which carry a `vault` tag. The
 * WLP4xxx collaborations White Labs propagates for The Yeast Bay live in
 * `yeast-bay.ts` under that lab's name.
 *
 * White Labs publishes fermentation ranges in °F and °C; the °C figure is
 * taken as published (PLAN §5 canon). Attenuation is apparent attenuation.
 *
 * `alcoholTolerancePercent` keeps the upper figure of White Labs' band — Low =
 * 2-5 %, Medium = 5-10 %, Medium-High = 8-12 %, High = 10-15 %. "Very High
 * (15%+)" has no upper bound, so it is recorded as the 15 % they do guarantee,
 * and "Varies" is `null`.
 *
 * Flocculation uses the labs' shared five-step vocabulary; White Labs' "Very
 * High" maps onto `high`.
 *
 * White Labs prints an STA1 QC result on every strain page, so `sta1` here is
 * a real test result rather than a guess — `null` only where they leave the
 * field blank.
 *
 * Twelve catalogue entries are deliberately absent: WLP019, WLP600, WLP616,
 * WLP669, WLP673, WLP675, WLP678, WLP685, WLP690, WLP692, WLP693 and WLP700
 * are listed with no attenuation, temperature or flocculation at all (several
 * are kombucha, malolactic or acetic cultures rather than brewing yeast), and
 * we do not invent three core figures to make a row.
 */

import type { Yeast } from '../../schema/yeast';

/** White Labs cultures. @see Yeast */
export const whiteLabsYeasts: readonly Yeast[] = [
  {
    id: 'white-labs-wlp001',
    lab: 'White Labs',
    productCode: 'WLP001',
    name: 'California Ale Yeast®',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 85 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Chico strain in a White Labs vial: clean, neutral and forgiving, and the reason so many American pale ales taste of hops rather than yeast. It attenuates further than most brewers expect, so pitch it healthy and let it finish.',
    tags: ['clean', 'neutral', 'american', 'pale-ale', 'ipa', 'house-strain', 'chico'],
    url: 'https://www.whitelabs.com/yeast-single?id=101&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp002',
    lab: 'White Labs',
    productCode: 'WLP002',
    name: 'English Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 63, max: 70 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A very flocculent English strain that leaves malt and fruit behind and drops out fast. It under-attenuates by design, which is what gives an ESB its body, but it can quit early if you leave it cold and undisturbed.',
    tags: ['english', 'esb', 'malty', 'fruity', 'flocculent', 'cask', 'low-attenuation'],
    url: 'https://www.whitelabs.com/yeast-single?id=102&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp003',
    lab: 'White Labs',
    productCode: 'WLP003',
    name: 'German Ale II',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A clean German ale strain for kölsch, altbier and German-style pale ales. It attenuates further than WLP029 while staying just as neutral.',
    tags: ['german', 'kolsch', 'alt', 'clean', 'pale-ale', 'flocculent', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=103&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp004',
    lab: 'White Labs',
    productCode: 'WLP004',
    name: 'Irish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 74 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'From one of the oldest stout breweries in the world, and it shows: slightly dry with a light fruit ester that suits roast barley. It flocculates well and clears without much help.',
    tags: ['irish', 'stout', 'dry', 'esters', 'flocculent', 'roast'],
    url: 'https://www.whitelabs.com/yeast-single?id=104&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp005',
    lab: 'White Labs',
    productCode: 'WLP005',
    name: 'British Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 74 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A malty English strain built for traditional grists — Maris Otter, crystal, a little chocolate. It flocculates hard and stops short of dry, leaving the malt where you put it.',
    tags: ['english', 'malty', 'flocculent', 'bitter', 'maris-otter', 'traditional'],
    url: 'https://www.whitelabs.com/yeast-single?id=105&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp006',
    lab: 'White Labs',
    productCode: 'WLP006',
    name: 'Bedford British Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Bedford strain that ferments dry and still leaves a full mouthfeel, with a distinct ester profile over the top. It flocculates very well, so bright beer comes easily.',
    tags: ['english', 'bedford', 'dry', 'esters', 'flocculent', 'mouthfeel', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=106&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp007',
    lab: 'White Labs',
    productCode: 'WLP007',
    name: 'Crisp English Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Formerly Dry English Ale, and still the most attenuative of the English strains: it will reach 80% even at 10% ABV. Crisp and clean at the finish, which makes it a good choice for strong English ales that would otherwise cloy.',
    tags: ['english', 'dry', 'attenuative', 'crisp', 'flocculent', 'strong-ale'],
    url: 'https://www.whitelabs.com/yeast-single?id=107&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp008',
    lab: 'White Labs',
    productCode: 'WLP008',
    name: 'East Coast Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An East Coast American strain, neutral enough for classic styles and increasingly used for hazy IPA. It flocculates poorly, which is a feature rather than a fault in that context.',
    tags: ['american', 'east-coast', 'neutral', 'hazy', 'ipa', 'low-floc'],
    url: 'https://www.whitelabs.com/yeast-single?id=108&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp009',
    lab: 'White Labs',
    productCode: 'WLP009',
    name: 'Australian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An Australian strain that ferments clean and malty with a bready ester behind it. Highly flocculent, so it clears quickly once fermentation is done.',
    tags: ['australian', 'clean', 'malty', 'bready', 'flocculent', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=109&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp011',
    lab: 'White Labs',
    productCode: 'WLP011',
    name: 'European Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 70 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A malty northern European strain that stops around 70%, leaving plenty of malt behind. Good for altbier, kölsch-style ales, malty English ales and fruit beers.',
    tags: ['european', 'malty', 'alt', 'kolsch', 'fruit-beer', 'low-attenuation', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=110&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp013',
    lab: 'White Labs',
    productCode: 'WLP013',
    name: 'London Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 75 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A London strain with a distinctive oak-like ester that suits bitters, porters and stouts. It finishes drier than WLP002 and stays in suspension longer.',
    tags: ['english', 'london', 'oak', 'esters', 'dry', 'bitter', 'porter'],
    url: 'https://www.whitelabs.com/yeast-single?id=111&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp017',
    lab: 'White Labs',
    productCode: 'WLP017',
    name: 'Whitbread II Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 73 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A traditional mixed British culture, slightly fruity with a hint of sulfur that conditions out. Highly flocculent, and closer to a cask profile than a modern single strain.',
    tags: ['english', 'whitbread', 'mixed-culture', 'fruity', 'sulfur', 'flocculent', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=112&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp022',
    lab: 'White Labs',
    productCode: 'WLP022',
    name: 'Essex Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 76 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A flavorful British strain with fruity and bready character, and enough flocculation to clear on its own. A good bitter yeast when WLP002 would leave the beer too sweet.',
    tags: ['english', 'essex', 'fruity', 'bready', 'balanced', 'bitter', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=114&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp023',
    lab: 'White Labs',
    productCode: 'WLP023',
    name: 'Burton Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'From Burton upon Trent, the town that put IPA on the map. It attenuates well and runs a little warmer than most English strains, which suits a hop-forward pale ale.',
    tags: ['english', 'burton', 'ipa', 'mineral', 'attenuative', 'pale-ale', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=115&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp025',
    lab: 'White Labs',
    productCode: 'WLP025',
    name: 'Southwold Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 19, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Suffolk strain giving complex fruit, citrus and spice, and a narrow temperature band it likes to be held in. Best in British bitters and old ales where that complexity has somewhere to go.',
    tags: ['english', 'southwold', 'fruit', 'citrus', 'spice', 'bitter', 'complex', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=116&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp026',
    lab: 'White Labs',
    productCode: 'WLP026',
    name: 'Premium Bitter Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'A Staffordshire strain giving a mild but complex ester character, suited to every English style from bitter to barleywine. It carries STA1, which is unusual for an English ale yeast and worth knowing.',
    tags: ['english', 'staffordshire', 'bitter', 'esb', 'esters', 'sta1', 'complex', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=117&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp028',
    lab: 'White Labs',
    productCode: 'WLP028',
    name: 'Edinburgh/Scottish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Edinburgh strain, with underlying pear and melon esters that sit well under peated or heavily malted grists. It takes a strong Scottish ale without flagging.',
    tags: ['scottish', 'edinburgh', 'malty', 'pear', 'melon', 'strong-ale'],
    url: 'https://www.whitelabs.com/yeast-single?id=118&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp029',
    lab: 'White Labs',
    productCode: 'WLP029',
    name: 'Kölsch Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'From a small Cologne brewpub, and the standard kölsch and altbier choice. Ferment it cool and lager it cold and it finishes crisp with only a whisper of fruit.',
    tags: ['kolsch', 'german', 'alt', 'clean', 'crisp', 'hybrid'],
    url: 'https://www.whitelabs.com/yeast-single?id=119&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp030',
    lab: 'White Labs',
    productCode: 'WLP030',
    name: 'Thames Valley Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A very flocculent Thames Valley strain for porters, stouts and bitters. White Labs publish no fermentation temperature for it, so the band here is class-typical rather than theirs.',
    tags: ['english', 'thames-valley', 'flocculent', 'porter', 'stout', 'bitter', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=120&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp033',
    lab: 'White Labs',
    productCode: 'WLP033',
    name: 'Klassic Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 66, max: 74 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A traditional English single strain for bitters, milds, porters and stouts, and it takes a Scottish ale too. It stops around 74%, keeping the malt in place.',
    tags: ['english', 'klassic', 'bitter', 'mild', 'porter', 'stout', 'scottish', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=121&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp036',
    lab: 'White Labs',
    productCode: 'WLP036',
    name: 'Dusseldorf Alt Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Düsseldorf altbier strain for clean, malty German browns and ambers. It keeps the malt in front and leaves very little of itself behind.',
    tags: ['alt', 'german', 'dusseldorf', 'malty', 'clean', 'amber', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=122&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp037',
    lab: 'White Labs',
    productCode: 'WLP037',
    name: 'Yorkshire Square Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Yorkshire square strain giving toasty, malt-driven esters and a well-balanced beer. It is highly flocculent, so rouse it if the gravity stalls before you meant it to.',
    tags: ['english', 'yorkshire', 'malty', 'toasty', 'flocculent', 'balanced', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=123&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp039',
    lab: 'White Labs',
    productCode: 'WLP039',
    name: 'East Midlands Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 82 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A British strain with an unusually dry finish for its family, attenuating to 82%. Good across pale ales, ambers, porters and stouts where you want English character without residual sweetness.',
    tags: [
      'english',
      'east-midlands',
      'dry',
      'attenuative',
      'pale-ale',
      'porter',
      'stout',
      'vault',
    ],
    url: 'https://www.whitelabs.com/yeast-single?id=125&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp041',
    lab: 'White Labs',
    productCode: 'WLP041',
    name: 'Pacific Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Pacific Northwest strain that keeps ester low and malt character forward, and flocculates hard. A useful middle ground between the Chico strain and an English yeast.',
    tags: [
      'american',
      'pacific-northwest',
      'malty',
      'mild-ester',
      'flocculent',
      'versatile',
      'vault',
    ],
    url: 'https://www.whitelabs.com/yeast-single?id=126&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp045',
    lab: 'White Labs',
    productCode: 'WLP045',
    name: 'Scotch Whisky',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 22, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A Scotch whisky strain in use since the early 1950s, producing the complex ester array a malt wash needs. It carries STA1, which matters far less in a still than in a bottle.',
    tags: ['whisky', 'scotch', 'distilling', 'esters', 'sta1', 'high-gravity', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=127&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp050',
    lab: 'White Labs',
    productCode: 'WLP050',
    name: 'Tennessee Whiskey',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 24, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Tennessee whiskey strain for American whiskeys and bourbons, known for rich, smooth flavors. It runs warm, well above ale temperatures.',
    tags: ['whiskey', 'bourbon', 'distilling', 'smooth', 'rich', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=128&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp051',
    lab: 'White Labs',
    productCode: 'WLP051',
    name: 'California V Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Closer to an English strain than to WLP001, with more fruit and better flocculation. A good choice when a California ale needs a little more character than the Chico strain gives.',
    tags: ['american', 'california', 'fruity', 'flocculent', 'english-like', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=129&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp059',
    lab: 'White Labs',
    productCode: 'WLP059',
    name: 'Melbourne Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 23, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'An Australian isolate from the early 1900s, clean-fermenting and suited to both American and English styles. It likes a warm, steady fermentation rather than a cool one.',
    tags: ['australian', 'melbourne', 'clean', 'versatile', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=130&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp060',
    lab: 'White Labs',
    productCode: 'WLP060',
    name: 'American Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'Three strains blended for a clean, neutral fermentation across American styles. It gives the reliability of a blend without much of a signature of its own.',
    tags: ['american', 'blend', 'clean', 'neutral', 'versatile', 'pale-ale', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=131&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp064',
    lab: 'White Labs',
    productCode: 'WLP064',
    name: 'Buchner Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A two-strain blend that adds creaminess with a crisp edge behind it. Aimed at pale ales, cream ales and American wheats.',
    tags: ['american', 'blend', 'creamy', 'crisp', 'cream-ale', 'wheat', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=132&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp065',
    lab: 'White Labs',
    productCode: 'WLP065',
    name: 'American Whiskey',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 24, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'An American whiskey strain with a low ester profile and moderate fusel production, for barley or corn washes. Tolerant of both heat and alcohol, which a distilling ferment needs.',
    tags: [
      'whiskey',
      'american',
      'distilling',
      'neutral',
      'fusel',
      'warm',
      'high-gravity',
      'vault',
    ],
    url: 'https://www.whitelabs.com/yeast-single?id=133&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp066',
    lab: 'White Labs',
    productCode: 'WLP066',
    name: 'London Fog Ale Yeast®',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The London Fog strain, bred for hazy IPA: fruity, low-flocculating and content to stay in suspension. It attenuates further than the London strains it descends from.',
    tags: ['neipa', 'hazy', 'london', 'fruity', 'low-floc', 'ipa'],
    url: 'https://www.whitelabs.com/yeast-single?id=134&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp067',
    lab: 'White Labs',
    productCode: 'WLP067',
    name: 'Coastal Haze Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      "A blend of White Labs' New England IPA strains, giving haze and tropical fruit together. It carries STA1, so treat packaged beer as capable of drying out further.",
    tags: ['neipa', 'hazy', 'blend', 'tropical', 'sta1', 'ipa'],
    url: 'https://www.whitelabs.com/yeast-single?id=135&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp070',
    lab: 'White Labs',
    productCode: 'WLP070',
    name: 'Kentucky Bourbon',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 22, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Kentucky bourbon distillery strain producing malty caramel character with balanced esters. Built for a warm, fast, high-gravity fermentation.',
    tags: ['bourbon', 'distilling', 'malty', 'caramel', 'esters', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=136&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp072',
    lab: 'White Labs',
    productCode: 'WLP072',
    name: 'French Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A clean French strain that complements malt rather than competing with it, and flocculates well. It works across an unusually wide temperature range for an ale yeast.',
    tags: ['french', 'clean', 'malty', 'flocculent', 'versatile', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=137&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp073',
    lab: 'White Labs',
    productCode: 'WLP073',
    name: 'Artisanal Country Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A classic bière de garde strain, with slight esters and mild phenols over the malt sweetness it is careful to preserve. It carries STA1 and will keep attenuating given time.',
    tags: ['biere-de-garde', 'malty', 'phenolic', 'esters', 'sta1', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=138&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp075',
    lab: 'White Labs',
    productCode: 'WLP075',
    name: 'Hansen Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A blended ale culture with good flocculation and a high alcohol tolerance. A dependable house option when a single strain would be too narrow.',
    tags: ['blend', 'american', 'flocculent', 'versatile', 'high-gravity', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=139&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp076',
    lab: 'White Labs',
    productCode: 'WLP076',
    name: 'Old Sonoma Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'From a historic Northern California brewery, and one of the strains American craft brewing was built on. English in character rather than Chico-clean.',
    tags: ['american', 'california', 'historic', 'english-style', 'malty', 'craft', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=140&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp077',
    lab: 'White Labs',
    productCode: 'WLP077',
    name: 'Tropicale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A blend built for tropical fruit character and a soft, low-flocculating finish. It suits hazy and fruit-forward beers where clarity is not the goal.',
    tags: ['blend', 'tropical', 'hazy', 'ipa', 'fruity', 'low-floc'],
    url: 'https://www.whitelabs.com/yeast-single?id=267&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp078',
    lab: 'White Labs',
    productCode: 'WLP078',
    name: 'Neutral Grain',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 84 },
    temperatureC: { min: 24, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A clean, fast strain for neutral grain spirit, tolerant of heat and alcohol. It attenuates to 84% and leaves nothing behind, which is the specification for a vodka wash.',
    tags: ['spirits', 'neutral-grain', 'clean', 'fast', 'warm', 'high-gravity', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=141&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp080',
    lab: 'White Labs',
    productCode: 'WLP080',
    name: 'Cream Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "A blend of an ale and a lager yeast, which is the traditional route to a cream ale. It ferments at ale temperatures and finishes with a lager's crispness.",
    tags: ['cream-ale', 'blend', 'ale', 'lager', 'clean', 'crisp', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=142&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp085',
    lab: 'White Labs',
    productCode: 'WLP085',
    name: 'English Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 76 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A blend of British ale strains put together to add complexity and attenuation to an English ale. More interesting than any of its parts, and it still drops well.',
    tags: ['english', 'blend', 'complex', 'attenuative', 'bitter', 'flocculent', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=143&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp090',
    lab: 'White Labs',
    productCode: 'WLP090',
    name: 'San Diego Super Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 83 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The San Diego Super strain: a fast, low-ester fermenter with a profile close to WLP001 but more attenuative. Reach for it when the schedule is tight and the beer should taste of hops.',
    tags: ['american', 'san-diego', 'clean', 'fast', 'neutral', 'attenuative', 'ipa'],
    url: 'https://www.whitelabs.com/yeast-single?id=144&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp095',
    lab: 'White Labs',
    productCode: 'WLP095',
    name: 'Burlington Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The signature strain of a Northeast US brewery, and one of the original hazy IPA yeasts. Fruity and soft, and best pitched into a beer that will be drunk young.',
    tags: ['neipa', 'hazy', 'burlington', 'ipa', 'fruity', 'vermont', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=145&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp096',
    lab: 'White Labs',
    productCode: 'WLP096',
    name: 'Franken',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A blend that attenuates hard and tolerates high alcohol, carrying STA1. Useful for strong beers meant to finish dry, and worth keeping away from anything you want stable in bottle.',
    tags: ['blend', 'attenuative', 'sta1', 'high-gravity', 'dry', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=317&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp099',
    lab: 'White Labs',
    productCode: 'WLP099',
    name: 'Super High Gravity Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 100 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'An English strain that will ferment to 25% ABV if it is fed and stepped up properly, which makes it the barleywine and imperial stout choice. Esters climb with gravity, and it carries STA1.',
    tags: ['high-gravity', 'barleywine', 'sta1', 'esters', 'strong-ale', 'attenuative', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=146&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp101',
    lab: 'White Labs',
    productCode: 'WLP101',
    name: 'SuperCell Yeast Blend®',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 25, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A mixed culture for cider, seltzer and spirits rather than beer, producing a complex profile at temperatures where brewing yeast would give up. It carries STA1.',
    tags: ['blend', 'cider', 'seltzer', 'spirits', 'mixed-culture', 'sta1', 'warm'],
    url: 'https://www.whitelabs.com/yeast-single?id=341&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp300',
    lab: 'White Labs',
    productCode: 'WLP300',
    name: 'Hefeweizen Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The traditional German hefeweizen strain, and the reference for banana and clove. It flocculates poorly on purpose: the haze is the style.',
    tags: ['wheat', 'hefeweizen', 'banana', 'clove', 'german', 'low-floc'],
    url: 'https://www.whitelabs.com/yeast-single?id=148&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp320',
    lab: 'White Labs',
    productCode: 'WLP320',
    name: 'American Hefeweizen Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'An American wheat strain that ferments far cleaner than its hefeweizen cousins, leaving very little banana or clove. The right choice when the wheat should taste of wheat.',
    tags: ['wheat', 'american', 'clean', 'low-ester', 'low-floc'],
    url: 'https://www.whitelabs.com/yeast-single?id=149&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp351',
    lab: 'White Labs',
    productCode: 'WLP351',
    name: 'Bavarian Weizen Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'The former Yeast Lab W51, giving a classic German weizen balance of banana and clove. It carries STA1, so bottle-conditioned weizen wants watching.',
    tags: ['wheat', 'bavarian', 'banana', 'clove', 'sta1', 'low-floc'],
    url: 'https://www.whitelabs.com/yeast-single?id=150&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp380',
    lab: 'White Labs',
    productCode: 'WLP380',
    name: 'Hefeweizen IV Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A hefeweizen strain that leans phenolic rather than estery: nutmeg, clove and spice ahead of banana and bubblegum. The counterweight to WLP300 when the banana is too much.',
    tags: ['wheat', 'hefeweizen', 'clove', 'nutmeg', 'spice', 'phenolic'],
    url: 'https://www.whitelabs.com/yeast-single?id=151&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp400',
    lab: 'White Labs',
    productCode: 'WLP400',
    name: 'Belgian Wit Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The witbier strain, with high phenol production giving a herbal aroma and a tart finish. It is what carries coriander and orange peel rather than burying them.',
    tags: ['witbier', 'belgian', 'phenolic', 'herbal', 'tart', 'spice'],
    url: 'https://www.whitelabs.com/yeast-single?id=152&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp410',
    lab: 'White Labs',
    productCode: 'WLP410',
    name: 'Belgian Wit II Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A cleaner witbier strain with medium spice-like phenol production. Useful when WLP400 would push a wheat beer further into phenol than you want.',
    tags: ['witbier', 'belgian', 'clean', 'spicy', 'phenolic', 'moderate', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=153&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp500',
    lab: 'White Labs',
    productCode: 'WLP500',
    name: 'Monastery Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Belgian monastery strain with the alcohol tolerance for quads, tripels and dubbels. It stays in suspension a long while and rewards a slow, warming fermentation.',
    tags: ['belgian', 'monastery', 'trappist', 'high-gravity', 'dubbel', 'tripel', 'quad'],
    url: 'https://www.whitelabs.com/yeast-single?id=154&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp510',
    lab: 'White Labs',
    productCode: 'WLP510',
    name: 'Bastogne Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A high-gravity Belgian strain that finishes dry with a slightly acidic edge. The dryness is what keeps a big tripel drinkable.',
    tags: ['belgian', 'bastogne', 'dry', 'acidic', 'high-gravity', 'tripel', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=155&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp515',
    lab: 'White Labs',
    productCode: 'WLP515',
    name: 'Antwerp Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 19, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'An almost lager-clean Belgian strain for pale and amber ales, and a good base to blend other strains against. Very little phenol of its own.',
    tags: ['belgian', 'antwerp', 'clean', 'lager-like', 'pale-ale', 'blend-base', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=156&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp518',
    lab: 'White Labs',
    productCode: 'WLP518',
    name: 'Opshaug Kveik Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 69, max: 80 },
    temperatureC: { min: 25, max: 35 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'An Opshaug kveik collected by Lars Marius Garshol, fermenting fast and clean at temperatures that would wreck an ale yeast. It tolerates high gravity and finishes in days rather than weeks.',
    tags: ['kveik', 'opshaug', 'norwegian', 'fast', 'hot', 'clean', 'high-gravity'],
    url: 'https://www.whitelabs.com/yeast-single?id=157&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp519',
    lab: 'White Labs',
    productCode: 'WLP519',
    name: 'Stranda Kveik Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 22, max: 37 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Stranda kveik from Stein Langlo, attenuating hard across an enormous temperature band. Ferment it hot and it will be done before a normal ale has started.',
    tags: ['kveik', 'stranda', 'norwegian', 'hot', 'fast', 'attenuative', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=158&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp520',
    lab: 'White Labs',
    productCode: 'WLP520',
    name: 'Sigmund Kveik Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 83 },
    temperatureC: { min: 22, max: 37 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "Sigmund Gjernes' Voss kveik, and the one most brewers meet first: citrus and orange-peel character, fermenting hot and fast. It is very forgiving of temperature, which is the whole appeal.",
    tags: ['kveik', 'sigmund', 'voss', 'norwegian', 'citrus', 'hot', 'fast', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=159&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp521',
    lab: 'White Labs',
    productCode: 'WLP521',
    name: 'Hornindal Kveik Ale',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 22, max: 37 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Hornindal kveik from Terje Raftevold, giving intense tropical fruit at temperatures up to 37 °C. It flocculates well for a kveik, so it clears sooner than you would guess.',
    tags: ['kveik', 'hornindal', 'norwegian', 'tropical', 'hot', 'flocculent', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=160&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp530',
    lab: 'White Labs',
    productCode: 'WLP530',
    name: 'Abbey Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'The Westmalle-style abbey strain, and a mainstay for dubbels and tripels. It wants a warm finish and a lot of headspace.',
    tags: ['belgian', 'abbey', 'trappist', 'high-gravity', 'esters', 'phenolic'],
    url: 'https://www.whitelabs.com/yeast-single?id=161&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp540',
    lab: 'White Labs',
    productCode: 'WLP540',
    name: 'Abbey IV Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A fourth abbey strain, attenuating well and giving a balanced ester and phenol profile. A good all-rounder across the Belgian dark and strong styles.',
    tags: ['belgian', 'abbey', 'esters', 'phenolic', 'dubbel', 'tripel', 'attenuative'],
    url: 'https://www.whitelabs.com/yeast-single?id=162&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp545',
    lab: 'White Labs',
    productCode: 'WLP545',
    name: 'Belgian Strong Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'An Ardennes strain producing moderate fruity esters over a very dry finish, with the alcohol tolerance for a big beer. It carries STA1.',
    tags: [
      'belgian',
      'ardennes',
      'strong-ale',
      'esters',
      'phenolic',
      'sta1',
      'attenuative',
      'vault',
    ],
    url: 'https://www.whitelabs.com/yeast-single?id=163&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp546',
    lab: 'White Labs',
    productCode: 'WLP546',
    name: 'Marañón Canyon Wild Cacao',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 65, max: 70 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A wild yeast isolated from cacao in the Marañón Canyon in Peru. It attenuates lightly and brings an unusual fruit character; treat it as an experiment rather than a workhorse.',
    tags: ['wild', 'cacao', 'peru', 'low-attenuation', 'fruity', 'experimental', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=164&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp548',
    lab: 'White Labs',
    productCode: 'WLP548',
    name: 'Original Grodziskie',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 12, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A blend for Grodziskie, the smoked wheat ale that lives at 2.5–3.3% ABV. It works across an unusually wide temperature range for a historic style.',
    tags: ['grodziskie', 'blend', 'smoked', 'wheat', 'session', 'historic', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=342&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp550',
    lab: 'White Labs',
    productCode: 'WLP550',
    name: 'Belgian Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A very expressive Belgian strain, phenol-forward with clove and allspice. It ferments warm and attenuates deeply, which suits saisons and Belgian pale ales alike.',
    tags: ['belgian', 'phenolic', 'clove', 'allspice', 'saison', 'warm'],
    url: 'https://www.whitelabs.com/yeast-single?id=165&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp561',
    lab: 'White Labs',
    productCode: 'WLP561',
    name: 'Non STA1son® Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A blend of STA1-negative strains giving saison esters and phenols without the super-attenuation that usually comes with them. The point is a farmhouse beer that stays stable in package.',
    tags: ['saison', 'farmhouse', 'blend', 'sta1-free', 'esters', 'phenolic', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=166&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp564',
    lab: 'White Labs',
    productCode: 'WLP564',
    name: 'Leeuwenhoek Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'Two saison strains and a low-phenolic Belgian strain, blended for the saisons White Labs brew themselves. It carries STA1 and finishes in the low 80s.',
    tags: ['saison', 'blend', 'belgian', 'low-phenolic', 'sta1', 'farmhouse', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=167&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp565',
    lab: 'White Labs',
    productCode: 'WLP565',
    name: 'Belgian Saison I Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 75 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'The Dupont saison strain, famous for stalling around 1.030 and then finishing if you let it get properly hot. It carries STA1 and will keep going, so give it heat and patience.',
    tags: ['saison', 'dupont', 'phenolic', 'stalls', 'sta1', 'hot'],
    url: 'https://www.whitelabs.com/yeast-single?id=168&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp566',
    lab: 'White Labs',
    productCode: 'WLP566',
    name: 'Belgian Saison II Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A moderate phenol producer with clove-like flavor and aroma, and a temperature range up to 30 °C. It carries STA1 and attenuates to 85%.',
    tags: ['saison', 'belgian', 'clove', 'phenolic', 'sta1', 'warm', 'attenuative', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=169&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp568',
    lab: 'White Labs',
    productCode: 'WLP568',
    name: 'Belgian Style Saison Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'A blend of Belgian and saison strains giving pear esters over spicy, earthy, clove-like phenols. More predictable than a single saison strain, and it still carries STA1.',
    tags: ['saison', 'belgian', 'blend', 'pear', 'spicy', 'clove', 'sta1'],
    url: 'https://www.whitelabs.com/yeast-single?id=170&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp570',
    lab: 'White Labs',
    productCode: 'WLP570',
    name: 'Belgian Golden Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'The Belgian golden strong strain: dry, complex and alcohol-tolerant, with esters and phenols in balance. It carries STA1 and finishes far drier than the gravity suggests.',
    tags: [
      'belgian',
      'golden-strong',
      'dry',
      'esters',
      'phenolic',
      'sta1',
      'high-gravity',
      'vault',
    ],
    url: 'https://www.whitelabs.com/yeast-single?id=171&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp575',
    lab: 'White Labs',
    productCode: 'WLP575',
    name: 'Belgian Style Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      "Two monastery strains and a Belgian ale strain blended into one versatile culture. A sensible default when the recipe just says 'Belgian yeast'.",
    tags: ['belgian', 'blend', 'monastery', 'versatile', 'esters', 'phenolic'],
    url: 'https://www.whitelabs.com/yeast-single?id=172&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp585',
    lab: 'White Labs',
    productCode: 'WLP585',
    name: 'Belgian Saison III Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'A saison strain leaning hard into fruit ester with a mild tartness behind it. It carries STA1 and attenuates to 85%.',
    tags: ['saison', 'belgian', 'esters', 'tart', 'fruity', 'sta1', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=173&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp590',
    lab: 'White Labs',
    productCode: 'WLP590',
    name: 'French Saison Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A popular French saison strain giving pear and apple over a farmhouse base. It carries STA1 and takes a beer very dry, so plan the finishing gravity accordingly.',
    tags: ['saison', 'french', 'pear', 'apple', 'farmhouse', 'sta1', 'attenuative'],
    url: 'https://www.whitelabs.com/yeast-single?id=174&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp603',
    lab: 'White Labs',
    productCode: 'WLP603',
    name: 'Torulaspora delbrueckii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 20, max: 20 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Torulaspora delbrueckii, a maltose-negative yeast for low-alcohol beer or for blending with a higher-attenuating strain. On its own it stops around 20%.',
    tags: [
      'torulaspora',
      'low-alcohol',
      'maltose-negative',
      'blend-partner',
      'non-alcoholic',
      'vault',
    ],
    url: 'https://www.whitelabs.com/yeast-single?id=176&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp611',
    lab: 'White Labs',
    productCode: 'WLP611',
    name: 'New Nordic Ale',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 65, max: 75 },
    temperatureC: { min: 10, max: 30 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A blend of three yeasts isolated from spontaneously fermented apples on a Danish island in 2009. It works anywhere from 10 °C to 30 °C, which is a range no single strain covers.',
    tags: ['nordic', 'blend', 'wild', 'apple', 'denmark', 'wide-range', 'spontaneous', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=177&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp618',
    lab: 'White Labs',
    productCode: 'WLP618',
    name: 'NA All Day®',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 5, max: 20 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 5,
    sta1: false,
    description:
      'Saccharomycodes ludwigii, a maltose-negative yeast that leaves almost all the sugar behind. It is the usual route to a non-alcoholic beer that still tastes fermented.',
    tags: ['non-alcoholic', 'low-alcohol', 'maltose-negative', 'ludwigii', 'session'],
    url: 'https://www.whitelabs.com/yeast-single?id=179&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp630',
    lab: 'White Labs',
    productCode: 'WLP630',
    name: 'Berliner Weisse Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'A German weizen yeast and Lactobacillus together for a subtly tart Berliner weisse. It takes several weeks to reach the acidity you want, and it carries STA1.',
    tags: ['berliner-weisse', 'lacto', 'blend', 'sour', 'tart', 'sta1', 'aging'],
    url: 'https://www.whitelabs.com/yeast-single?id=180&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp631',
    lab: 'White Labs',
    productCode: 'WLP631',
    name: 'Appalachian Tart',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 32, max: 37 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A souring culture that works at 32–37 °C and attenuates like a yeast rather than a bacterium. Built for a fast, warm sour without a separate souring step.',
    tags: ['sour', 'lactic', 'appalachian', 'hot', 'fast', 'kettle-sour', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=239&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp644',
    lab: 'White Labs',
    productCode: 'WLP644',
    name: 'Saccharomyces brux-like Trois',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 85 },
    temperatureC: { min: 21, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'The strain long sold as Brettanomyces Trois, since reclassified as a Saccharomyces: slightly tart with delicate mango and pineapple. It carries STA1, which is where the reputation for endless attenuation comes from.',
    tags: ['brett-like', 'trois', 'tart', 'mango', 'pineapple', 'sta1', 'primary', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=182&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp645',
    lab: 'White Labs',
    productCode: 'WLP645',
    name: 'Brettanomyces claussenii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 30, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The mildest of the Brettanomyces, isolated from English stock beer: low-intensity funk with a fruity edge. Best in secondary alongside a primary yeast.',
    tags: ['brett', 'claussenii', 'mild', 'fruity', 'secondary', 'english'],
    url: 'https://www.whitelabs.com/yeast-single?id=183&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp648',
    lab: 'White Labs',
    productCode: 'WLP648',
    name: 'Brettanomyces bruxellensis Trois Vrai',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 21, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      "The 'true' Brettanomyces bruxellensis Trois, meant for primary fermentation rather than as a secondary character. It works faster than most Brett and gives a tropical rather than barnyard profile.",
    tags: ['brett', 'bruxellensis', 'trois', 'primary', 'tropical', 'sta1-unknown'],
    url: 'https://www.whitelabs.com/yeast-single?id=184&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp650',
    lab: 'White Labs',
    productCode: 'WLP650',
    name: 'Brettanomyces bruxellensis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 30, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The classic secondary Brettanomyces for Belgian styles: medium-intensity, earth-forward funk that builds over months. Pitch it after primary and leave it alone.',
    tags: ['brett', 'bruxellensis', 'secondary', 'earthy', 'lambic', 'aging'],
    url: 'https://www.whitelabs.com/yeast-single?id=185&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp653',
    lab: 'White Labs',
    productCode: 'WLP653',
    name: 'Brettanomyces lambicus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 30, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Brettanomyces lambicus, the pie-cherry and barnyard end of the family. It is the most assertive of the White Labs Bretts and wants a long conditioning period.',
    tags: ['brett', 'lambicus', 'cherry', 'barnyard', 'secondary', 'aging'],
    url: 'https://www.whitelabs.com/yeast-single?id=186&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp661',
    lab: 'White Labs',
    productCode: 'WLP661',
    name: 'Pediococcus damnosus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 21, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Pediococcus damnosus, for sour programs that need acidity beyond what Lactobacillus gives. Expect a ropy phase partway through; Brettanomyces will clean it up if you are patient.',
    tags: ['pedio', 'pediococcus', 'sour', 'lactic', 'ropiness', 'aging'],
    url: 'https://www.whitelabs.com/yeast-single?id=188&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp665',
    lab: 'White Labs',
    productCode: 'WLP665',
    name: 'Flemish Ale Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 85 },
    temperatureC: { min: 20, max: 27 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A blend for the West Flanders styles — Flanders red and oud bruin. It attenuates hard and needs a year or more in wood or glass to develop.',
    tags: ['flanders', 'sour', 'blend', 'oud-bruin', 'red', 'aging', 'attenuative', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=189&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp670',
    lab: 'White Labs',
    productCode: 'WLP670',
    name: 'American Farmhouse Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: true,
    description:
      'An American farmhouse blend aimed at semi-traditional Belgian-style ales, with moderate funk over a saison base. It carries STA1.',
    tags: ['farmhouse', 'american', 'blend', 'brett', 'saison', 'sta1', 'complex'],
    url: 'https://www.whitelabs.com/yeast-single?id=191&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp672',
    lab: 'White Labs',
    productCode: 'WLP672',
    name: 'Lactobacillus brevis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 21, max: 42 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Lactobacillus brevis for kettle souring or traditional souring, happy anywhere from 21 °C up to 42 °C. Keep the wort's IBU low and hold it warm.",
    tags: ['lacto', 'brevis', 'sour', 'kettle-sour', 'tart', 'warm'],
    url: 'https://www.whitelabs.com/yeast-single?id=192&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp677',
    lab: 'White Labs',
    productCode: 'WLP677',
    name: 'Lactobacillus delbrueckii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 21, max: 43 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Lactobacillus delbrueckii, giving the moderate acidity found in lambics, Berliner weisse and sour browns. It likes real heat, well above fermentation temperature.',
    tags: ['lacto', 'delbrueckii', 'sour', 'berliner-weisse', 'lambic', 'warm', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=195&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp686',
    lab: 'White Labs',
    productCode: 'WLP686',
    name: 'Zygosaccharomyces lentus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 5, max: 20 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Zygosaccharomyces lentus, a maltose-negative yeast for low-alcohol beer. Like WLP618, it leaves the fermentable sugar largely untouched.',
    tags: ['low-alcohol', 'maltose-negative', 'zygosaccharomyces', 'non-alcoholic', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=197&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp705',
    lab: 'White Labs',
    productCode: 'WLP705',
    name: 'Sake Yeast #7',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 21, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The Saké #7 strain, giving a full-bodied character with subtle fruity esters. For rice fermentations with koji, and tolerant of the alcohol they reach.',
    tags: ['sake', 'rice', 'full-bodied', 'esters', 'high-alcohol', 'koji'],
    url: 'https://www.whitelabs.com/yeast-single?id=201&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp707',
    lab: 'White Labs',
    productCode: 'WLP707',
    name: 'California Pinot Noir Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Pinot Noir isolate from Davis, California, producing fruity and complex aromas. It is reliable in difficult fermentations, which is why it turns up in country wines too.',
    tags: ['wine', 'red', 'pinot-noir', 'fruity', 'complex', 'reliable', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=202&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp709',
    lab: 'White Labs',
    productCode: 'WLP709',
    name: 'Sake #9',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 17, max: 20 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The Saké #9 strain, used in ginjo-shu production for its aromatic character. For rice-based fermentations with koji rather than malt.',
    tags: ['sake', 'rice', 'ginjo', 'koji', 'aromatic', 'high-alcohol', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=203&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp715',
    lab: 'White Labs',
    productCode: 'WLP715',
    name: 'Champagne',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 21, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The classic champagne strain: neutral, strong and very alcohol-tolerant, letting the fruit carry the aroma. Useful in wine, cider and mead, and for restarting a stuck fermentation.',
    tags: ['champagne', 'wine', 'cider', 'mead', 'neutral', 'strong', 'dry', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=204&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp718',
    lab: 'White Labs',
    productCode: 'WLP718',
    name: 'Avize Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A champagne isolate from Avize used to add complexity to whites, especially barrel-fermented Chardonnay. More elegant and less neutral than WLP715.',
    tags: ['wine', 'white', 'chardonnay', 'champagne', 'barrel', 'elegant', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=205&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp720',
    lab: 'White Labs',
    productCode: 'WLP720',
    name: 'Sweet Mead/Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 21, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A sweet mead and wine strain that leaves more residual sugar than the champagne strain, with a slightly fruity aroma. The usual choice when a mead should not finish bone dry.',
    tags: ['mead', 'wine', 'sweet', 'fruity', 'residual-sugar', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=206&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp727',
    lab: 'White Labs',
    productCode: 'WLP727',
    name: 'Steinberg-Geisenheim Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 10, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A German wine strain with high fruit ester production and real cold tolerance, fermenting down to 10 °C. Good for aromatic whites fermented slowly.',
    tags: ['wine', 'white', 'german', 'cold-tolerant', 'fruity', 'esters', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=207&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp730',
    lab: 'White Labs',
    productCode: 'WLP730',
    name: 'Chardonnay White Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 10, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A dry white wine strain with slight ester production and low sulfur dioxide, which lets varietal character through. Cold-tolerant like the other German-descended strains.',
    tags: ['wine', 'white', 'chardonnay', 'dry', 'low-sulfur', 'varietal', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=208&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp735',
    lab: 'White Labs',
    productCode: 'WLP735',
    name: 'French White Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A classic French white wine strain that gives an enhanced creamy texture. It foams little, which matters more than it sounds in a full fermenter.',
    tags: ['wine', 'white', 'french', 'creamy', 'low-foam', 'texture'],
    url: 'https://www.whitelabs.com/yeast-single?id=209&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp740',
    lab: 'White Labs',
    productCode: 'WLP740',
    name: 'Merlot Red Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A neutral Merlot strain with low fusel alcohol production that ferments dry. It carries STA1, which is worth knowing if the same equipment sees beer.',
    tags: ['wine', 'red', 'merlot', 'neutral', 'dry', 'low-fusel', 'sta1', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=210&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp749',
    lab: 'White Labs',
    productCode: 'WLP749',
    name: 'Assmanshausen Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 10, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A German red wine strain, cold-tolerant and producing spicy, fruity aromas. Assmanshausen is Pinot Noir country, and it shows.',
    tags: ['wine', 'red', 'german', 'cold-tolerant', 'spicy', 'fruity', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=211&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp750',
    lab: 'White Labs',
    productCode: 'WLP750',
    name: 'French Red Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The classic Bordeaux strain, with a rich, smooth flavor profile and a very high alcohol tolerance. A default for structured reds.',
    tags: ['wine', 'red', 'bordeaux', 'rich', 'smooth', 'high-alcohol', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=212&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp760',
    lab: 'White Labs',
    productCode: 'WLP760',
    name: 'Cabernet Red Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A versatile Cabernet strain for full-bodied reds, with enough ester production to suit dry aromatic whites as well. One of the more forgiving wine yeasts.',
    tags: ['wine', 'red', 'cabernet', 'full-bodied', 'esters', 'versatile', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=213&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp770',
    lab: 'White Labs',
    productCode: 'WLP770',
    name: 'Suremain Burgundy Wine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 90 },
    temperatureC: { min: 16, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Burgundy strain that emphasizes fruit aroma in barrel fermentations. It has a high nutrient requirement; underfeed it and you will get volatile acidity instead.',
    tags: ['wine', 'red', 'burgundy', 'barrel', 'fruity', 'nutrient-hungry', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=214&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp773',
    lab: 'White Labs',
    productCode: 'WLP773',
    name: 'Scottish Cider',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Two ale strains and a wine strain blended for cider, and unusual in not drying the fruit out completely. It leaves enough sweetness for the apple to stay recognizable.',
    tags: ['cider', 'blend', 'scottish', 'residual-sweetness', 'balanced', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=215&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp775',
    lab: 'White Labs',
    productCode: 'WLP775',
    name: 'English Cider',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The classic English cider strain: it ferments dry but keeps the apple character intact. A high alcohol tolerance means it will not stall in a strong cider.',
    tags: ['cider', 'dry', 'apple', 'classic', 'high-alcohol'],
    url: 'https://www.whitelabs.com/yeast-single?id=216&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp780',
    lab: 'White Labs',
    productCode: 'WLP780',
    name: 'Thai Rice Chong',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 70 },
    temperatureC: { min: 21, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A Thai rice-wine culture that also ferments beer perfectly well. White Labs publishes no flocculation figure for it, so the value here is class-typical rather than theirs.',
    tags: ['rice-wine', 'thai', 'experimental', 'beer', 'low-attenuation', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=217&type=YEAST',
    provenance: 'representative',
  },
  {
    id: 'white-labs-wlp800',
    lab: 'White Labs',
    productCode: 'WLP800',
    name: 'Pilsner Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'A Czech pilsner strain that finishes dry and crisp with a faint malty sweetness left behind. Slower than the German lager strains and worth a long, cold conditioning period before it shows its best.',
    tags: ['lager', 'czech', 'pilsner', 'crisp', 'dry', 'cold-conditioning'],
    url: 'https://www.whitelabs.com/yeast-single?id=218&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp802',
    lab: 'White Labs',
    productCode: 'WLP802',
    name: 'Czech Budejovice Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Czech Budejovice strain, from the southern Czech Republic, giving dry and crisp lagers with low diacetyl. It will reach 80% attenuation given time, which is what leaves the hop bitterness rounded rather than sharp.',
    tags: ['lager', 'czech', 'budejovice', 'pilsner', 'dry', 'crisp', 'low-diacetyl'],
    url: 'https://www.whitelabs.com/yeast-single?id=219&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp808',
    lab: 'White Labs',
    productCode: 'WLP808',
    name: 'Mythical Hammer Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 79 },
    temperatureC: { min: 10, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A lager blend that works across an unusually wide temperature band, from proper lager cold up to 18 °C. Useful when the fermentation fridge is doing two jobs at once.',
    tags: ['lager', 'blend', 'wide-range', 'clean', 'versatile', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=321&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp810',
    lab: 'White Labs',
    productCode: 'WLP810',
    name: 'San Francisco Lager',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 14, max: 18 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The San Francisco lager strain: a lager yeast that ferments at 14–18 °C and still tastes like a lager. It flocculates hard, which is why steam beer clears so readily.',
    tags: ['california-common', 'steam', 'lager', 'warm', 'flocculent', 'malty'],
    url: 'https://www.whitelabs.com/yeast-single?id=220&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp815',
    lab: 'White Labs',
    productCode: 'WLP815',
    name: 'Belgian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A clean, crisp European lager strain from an old brewery in west Belgium, notable for low sulfur production. Less conditioning time than most lager strains ask for.',
    tags: ['lager', 'belgian', 'clean', 'crisp', 'low-sulfur', 'european', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=221&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp820',
    lab: 'White Labs',
    productCode: 'WLP820',
    name: 'Oktoberfest/Märzen Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 65, max: 73 },
    temperatureC: { min: 11, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Oktoberfest and märzen strain, attenuating lightly so the malt keeps its sweetness and nuance. It is a slow fermenter; do not rush it warm to save a week.',
    tags: ['lager', 'oktoberfest', 'marzen', 'malty', 'residual-sweetness', 'low-attenuation'],
    url: 'https://www.whitelabs.com/yeast-single?id=222&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp826',
    lab: 'White Labs',
    productCode: 'WLP826',
    name: 'On the Pitch Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A blend of American, Canadian and Mexican lager yeasts chosen for clean performance and a balanced contribution. A safe pick for pale adjunct lagers.',
    tags: ['lager', 'blend', 'american', 'mexican', 'clean', 'balanced', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=372&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp830',
    lab: 'White Labs',
    productCode: 'WLP830',
    name: 'German Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The 34/70-style all-rounder: clean, dry and malt-balanced, and usable for anything from helles to schwarzbier. Needs a healthy pitch and a diacetyl rest, after which it is about as trouble-free as lager brewing gets.',
    tags: ['lager', 'german', 'helles', 'pilsner', 'clean', 'w34-70'],
    url: 'https://www.whitelabs.com/yeast-single?id=223&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp833',
    lab: 'White Labs',
    productCode: 'WLP833',
    name: 'German Bock Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'From the Alps of southern Bavaria, and balanced between malt and hop rather than leaning either way. The traditional bock strain, and it suits a doppelbock too.',
    tags: ['lager', 'german', 'bock', 'bavarian', 'balanced', 'malty'],
    url: 'https://www.whitelabs.com/yeast-single?id=224&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp835',
    lab: 'White Labs',
    productCode: 'WLP835',
    name: 'German X Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 10, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A German lager strain that likes the cold end of the range, 10–12 °C, and rewards being left there. Clean and malty, with none of the sulfur some German strains throw.',
    tags: ['lager', 'german', 'cold', 'clean', 'malty', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=225&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp838',
    lab: 'White Labs',
    productCode: 'WLP838',
    name: 'Southern German Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 68, max: 76 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A southern German strain with a malty finish, a balanced aroma and genuinely good flocculation. It clears faster than most lager yeasts, which shortens the tank time.',
    tags: ['lager', 'german', 'malty', 'flocculent', 'balanced', 'bavarian', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=226&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp840',
    lab: 'White Labs',
    productCode: 'WLP840',
    name: 'American Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The American lager strain: dry and clean with a light apple note, and minimal sulfur or diacetyl. It is the easiest lager yeast in the range to get a clean result from.',
    tags: ['lager', 'american', 'dry', 'clean', 'apple', 'low-sulfur', 'low-diacetyl'],
    url: 'https://www.whitelabs.com/yeast-single?id=227&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp845',
    lab: 'White Labs',
    productCode: 'WLP845',
    name: 'Fast Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 78 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A lager strain selected for speed, with low sulfur production and medium flocculation. The one to use when the tank is needed back.',
    tags: ['lager', 'fast', 'low-sulfur', 'clean', 'schedule', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=228&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp850',
    lab: 'White Labs',
    productCode: 'WLP850',
    name: 'Copenhagen Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A northern European strain that keeps malt flavor secondary and lets the hops and the fermentation character lead. Clean and crisp rather than malty.',
    tags: ['lager', 'copenhagen', 'clean', 'crisp', 'hop-forward', 'northern-european', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=229&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp860',
    lab: 'White Labs',
    productCode: 'WLP860',
    name: 'Munich Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 9, max: 11 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Munich helles strain, stopping around 72% so the malt keeps its sweetness. It wants the cold end of the range, 9–11 °C, and a long lagering.',
    tags: ['lager', 'munich', 'helles', 'malty', 'low-attenuation', 'cold'],
    url: 'https://www.whitelabs.com/yeast-single?id=230&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp885',
    lab: 'White Labs',
    productCode: 'WLP885',
    name: 'Zurich Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: true,
    description:
      'A Swiss lager strain that will take a beer past 11% ABV with care, which is unusual for a lager yeast. It carries STA1, so treat strong bottled lagers made with it accordingly.',
    tags: ['lager', 'swiss', 'high-gravity', 'strong', 'sta1', 'doppelbock', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=231&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp920',
    lab: 'White Labs',
    productCode: 'WLP920',
    name: 'Old Bavarian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 66, max: 73 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A southern German strain that finishes malty with a slight ester profile, made for Oktoberfests, bocks and dark lagers. It under-attenuates by design.',
    tags: ['lager', 'bavarian', 'malty', 'oktoberfest', 'bock', 'dark-lager', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=232&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp925',
    lab: 'White Labs',
    productCode: 'WLP925',
    name: 'High Pressure Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 82 },
    temperatureC: { min: 17, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'A high-pressure lager strain that ferments a lager in about a week at 17–20 °C. It needs the pressure to stay clean; run it warm and open and you will get an ale.',
    tags: ['lager', 'high-pressure', 'fast', 'warm', 'clean', 'schedule'],
    url: 'https://www.whitelabs.com/yeast-single?id=233&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp940',
    lab: 'White Labs',
    productCode: 'WLP940',
    name: 'Mexican Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 78 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Mexican lager strain, clean and crisp and equally at home in dark lagers, bocks and märzen. A good default when a lager should taste of malt rather than of yeast.',
    tags: ['lager', 'mexican', 'clean', 'crisp', 'dark-lager', 'bock'],
    url: 'https://www.whitelabs.com/yeast-single?id=234&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp1983',
    lab: 'White Labs',
    productCode: 'WLP1983',
    name: "Charlie's Fist Bump",
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 13, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'Licensed from Charlie Papazian, and unusual in fermenting cleanly at both ale and lager temperatures. One vial covers a range that would otherwise need two strains.',
    tags: ['versatile', 'ale', 'lager', 'wide-range', 'clean', 'charlie-papazian'],
    url: 'https://www.whitelabs.com/yeast-single?id=147&type=YEAST',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp6420',
    lab: 'White Labs',
    productCode: 'WLP6420',
    name: 'Acid Tripp',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 40, max: 43 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A souring culture that works at 40–43 °C, which is hotter than most Lactobacillus will tolerate. Built for fast kettle souring rather than a long mixed fermentation.',
    tags: ['lacto', 'sour', 'kettle-sour', 'hot', 'fast', 'acid', 'vault'],
    url: 'https://www.whitelabs.com/yeast-single?id=314&type=YEAST',
    provenance: 'representative',
  },
  // ---------------------------------------------------------------------------
  // Premium Active Dry — White Labs' dry line, produced with Lallemand. Three of
  // these are the same strains as the liquid entries above and keep their own
  // ids: the strain is one thing, the package another, and a brewer choosing
  // between them wants both on the shelf.
  // ---------------------------------------------------------------------------
  {
    id: 'white-labs-wlp001-dry',
    lab: 'White Labs',
    productCode: 'WLP001',
    name: 'Dry California Ale Yeast®',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 85 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: false,
    description:
      'The Chico strain in a sachet: the same clean, hop-forward workhorse as the vial, dried rather than propagated, and rated to 15 % so it will carry a big beer. White Labs say it is one of the few active dry yeasts you can repitch.',
    tags: ['clean', 'neutral', 'american', 'pale-ale', 'ipa', 'chico', 'dry'],
    url: 'https://4099054.app.netsuite.com/core/media/media.nl?id=10915622&c=4099054&h=yRUWoWwirPlqQtDqKtJwsRFAwjYOOZuF44HuBORaio3vRiHx&_xt=.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp066-dry',
    lab: 'White Labs',
    productCode: 'WLP066',
    name: 'Dry London Fog Ale Yeast®',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 17, max: 21 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The hazy-IPA strain dried: pineapple and ruby grapefruit over a low flocculation that keeps the beer turbid on purpose. It is the vial strain in a form that keeps on a shelf, which for a yeast this seasonal is most of the appeal.',
    tags: ['hazy', 'neipa', 'juicy', 'low-flocculation', 'tropical', 'dry'],
    url: 'https://4099054.app.netsuite.com/core/media/media.nl?id=10915510&c=4099054&h=nCD2JuE3i5Zyg4yH3XtnogORLSuxOvh9bkH-FIiMM22x9nBB&_xt=.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp079-dry',
    lab: 'White Labs',
    productCode: 'WLP079',
    name: 'Suava Ale Yeast™',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 72, max: 82 },
    temperatureC: { min: 24, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A strain bred for biotransformation rather than found in a brewery: White Labs, Escarpment and Lallemand together, non-GMO, haze positive, and happy up at 30 °C where it works through in under a week. Sold dry only, and the reason to reach for it is what it does to hop thiols rather than what it does to malt.',
    tags: ['biotransformation', 'thiol', 'hazy', 'haze-positive', 'warm', 'dry', 'suava'],
    url: 'https://4099054.app.netsuite.com/core/media/media.nl?id=15481392&c=4099054&h=YuPTCn4u2grziwI2B0UoQTDghsOIjomGfYuvPEBOb9j_h58l&_xt=.pdf',
    dosageGramsPerHl: { min: 75, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'white-labs-wlp860-dry',
    lab: 'White Labs',
    productCode: 'WLP860',
    name: 'Dry Munich Lager Yeast',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 9, max: 11 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: false,
    description:
      'The Munich lager strain dried, and it keeps the narrow cold band the liquid has: 9 to 11 °C, modest attenuation, malt left in the glass. The dry format is what makes a strain this cold-specific practical for a brewer who pitches it twice a year.',
    tags: ['lager', 'munich', 'helles', 'dunkel', 'malty', 'german', 'dry'],
    url: 'https://4099054.app.netsuite.com/core/media/media.nl?id=11206724&c=4099054&h=jObWTXh7oHdCIAr6qvfafLYUodNn_SYbdVC9-3-pge3dvgjc&_xt=.pdf',
    provenance: 'spec-sheet',
  },
];
